import {useEffect, useRef, useState} from "react";

/**
 * Reveal-on-scroll hook. Returns [ref, shown]; `shown` flips to true once the
 * element scrolls into view, then the observer disconnects (one-shot).
 * Falls back to visible when IntersectionObserver is unavailable or the user
 * prefers reduced motion, so content is never left hidden.
 */
export default function useReveal(threshold = 0.15) {
    const ref = useRef(null);
    const [shown, setShown] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el || shown) return;

        const reduce = typeof window !== "undefined" &&
            window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduce || typeof IntersectionObserver === "undefined") {
            setShown(true);
            return;
        }

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setShown(true);
                observer.disconnect();
            }
        }, {threshold, rootMargin: "0px 0px -6% 0px"});

        observer.observe(el);
        return () => observer.disconnect();
    }, [threshold, shown]);

    return [ref, shown];
}
